 
 
import { Component, Input, Output, EventEmitter, input, signal, effect, computed, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatNativeDateModule } from '@angular/material/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule, FormControl } from '@angular/forms';
import { DateTimeUtilService } from '../../../../../utilities/date-time-util.service';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-lt-datetimepicker-v2',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatInputModule,
    MatFormFieldModule,
    MatNativeDateModule,
    MatTimepickerModule,
    MatIconModule,
  ],
  providers: [
    DateTimeUtilService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LTDateTimePickerV2Component),
      multi: true,
    },
  ],
  template: `
    <div class="datetime-container">
      <mat-form-field [appearance]="fillMode" class="date-field">
        @if(dateLabel){
          <mat-label>{{dateLabel}}</mat-label>
        }
        <input matInput [matDatepicker]="picker"
              [value]="date()"
              [required]="required()"
              [min]="minDate()"
              [max]="maxDate()"
              (dateChange)="updateDate($event)"
              (click)="picker.open()"
              (blur)="onTouched()">
        <mat-hint>{{format}}</mat-hint>
        <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
        <mat-datepicker #picker></mat-datepicker>
      </mat-form-field>

      <mat-form-field appearance="outline" class="time-field">
        @if(timeLabel){
          <mat-label>{{timeLabel}}</mat-label>
        }
        <input matInput type="time"
              [formControl]="timeControl"
              [value]="time()"
              [required]="required()"
              (input)="updateTime($event)"
              (blur)="onTouched()">
      </mat-form-field>
    </div>
  `,
  styles: `
    .mdc-text-field__input::-webkit-calendar-picker-indicator {
      display: block !important;
    }
    `,
})
export class LTDateTimePickerV2Component implements ControlValueAccessor {
  //#region Inputs
  @Input() fillMode: 'outline' | 'fill' = 'outline';
  @Input() placeholder = 'Enter a date';
  @Input() clearButton = true;
  @Input() dateLabel = 'Date';
  @Input() timeLabel = 'Time';

  required = input(false);

  //#region MinDate
  private _minDate = signal<Date | null>(null);
  @Input() set min(value: Date | null) {
    this._minDate.set(value);
  }
  get minDate() {
    return this._minDate;
  }
  //#endregion

  //#region MaxDate
  private _maxDate = signal<Date | null>(null);
  @Input() set max(value: Date | null) {
    this._maxDate.set(value);
  }
  get maxDate() {
    return this._maxDate;
  }
  //#endregion

  //#region format
  private _format = signal('MM/dd/yyyy');
  showFormattedHint = false;
  @Input() set format(value: string) {
    if (value) {
      this._format.set(value);
      this.showFormattedHint = true;
    }
  }
  get format(): string {
    return this._format();
  }
  //#endregion

  //#region format
  private _timeFormat = signal('HH:mm');
  showTimeFormattedHint = false;
  @Input() set timeFormat(value: string) {
    if (value) {
      this._timeFormat.set(value);
      this.showTimeFormattedHint = true;
    }
  }
  get timeFormat(): string {
    return this._timeFormat();
  }
  //#endregion

  //#region Value
  private _value = signal<Date | null>(null);
  @Input() set value(value: Date | null) {
    this._value.set(value);
    this.dateControl.setValue(value, { emitEvent: false });
  }
  get value(): Date | null {
    return this._value();
  }
  //#endregion

  //#region Disabled
  @Input() set disabled(isDisabled: boolean) {
    if (isDisabled) {
      this.dateControl.disable();
    } else {
      this.dateControl.enable();
    }
  }
  get disabled(): boolean {
    return this.dateControl.disabled;
  }
  //#endregion

  //#endregion

  // Form control for the datepicker
  dateControl = new FormControl<Date | null>(null);
  timeControl = new FormControl<Date | null>(null);

  // ouput
  @Output() valueChange = new EventEmitter<Date | null>();

  // signals
  date = signal<Date | null>(null);
  time = signal<string>('');

  // Computed value combining date and time
  combinedDateTime = computed(() => {
    const currentDate = this.date();
    const currentTime = this.time();

    if (currentDate && currentTime) {
      return this.dateTimeUtil.combineDateAndTime(currentDate, currentTime);
    }
    return null;
  });

  // ControlValueAccessor
  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(private dateTimeUtil: DateTimeUtilService) {
    effect(() => {
      const datetime = this.combinedDateTime();
      if (datetime) {
        this.onChange(datetime);
        this.valueChange.emit(datetime);
      }
    });
  }

  updateDate(event: any): void {
    this.date.set(event.value);
  }

  updateTime(event: Event): void {
    const timeValue = (event.target as HTMLInputElement).value;
    this.time.set(timeValue);
  }

  writeValue(value: Date): void {
    if (value) {
      this.date.set(value);
      this.time.set(this.dateTimeUtil.formatTime(value));
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.dateControl.disable();
      this.timeControl.disable();
    } else {
      this.dateControl.enable();
      this.timeControl.enable();
    }
  }
}
